import { useGraphqlQuery } from "../query.utils/query.hooks"
import { useQuery } from "@tanstack/react-query"
import { gql } from "graphql-request"
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react"
import Fuse from "fuse.js"
import { Noop } from "../../types/noop"
import { useUi } from "../context/ui"

export type ApiGroup = {
  [key: string]: any
  categoryIds: number[]
}

export type ApiCategory = {
  [key: string]: any
  shortName: string
}

export type ApiPoint = {
  id: number
  name: string
  description: string | null
  categoryId: number
  geometry: { x: number; y: number }
  features: string | null
  media: any[]
}

export type ApiMedia = {
  id: number
  type: "youtube" | "image"
  url: string
}

export type NormalisedGroup = ApiGroup & {
  categories: ApiCategory[]
}

type MapDataInitialState = {
  searchResults: Fuse.FuseResult<ApiPoint>[]
  setSearchResults: Noop
}

const MapDataContext = createContext<MapDataInitialState>({} as MapDataInitialState)

export const MapDataProvider = ({ children }: { children: ReactNode }) => {
  // SEARCH
  const [searchResults, setSearchResults] = useState<Fuse.FuseResult<ApiPoint>[]>([])
  return (
    <MapDataContext.Provider
      value={{
        searchResults,
        setSearchResults,
      }}
    >
      {children}
    </MapDataContext.Provider>
  )
}

const useMapData = (staticDataUrl?: string) => {
  const { sidebarTab } = useUi()
  const { searchResults, setSearchResults } = useContext(MapDataContext)

  const gcTime = (import.meta.env.NODE_ENV === "development" ? 5 : 60 * 24) * 60 * 1000

  const staticQuery = useQuery<{ points: ApiPoint[]; categories: ApiCategory[]; groups: ApiGroup[]; medias: ApiMedia[] }>({
    queryKey: ["mapData-static", staticDataUrl],
    queryFn: () => fetch(staticDataUrl!).then((r) => r.json()),
    enabled: !!staticDataUrl,
    refetchOnWindowFocus: false,
    initialData: { points: [], categories: [], groups: [], medias: [] },
    gcTime,
  })

  const q = useGraphqlQuery<{ points: ApiPoint[]; categories: ApiCategory[]; groups: ApiGroup[]; medias: ApiMedia[] }>(
    "mapData",
    gql`
      query mapData {
        points {
          id
          categoryId
          name
          description
          geometry {
            x
            y
          }
          medias
        }
        categories {
          id
          name
          shortName
          description
          priority
          groupId
        }
        groups {
          id
          name
          description
          priority
          color
        }
        medias {
          id
          type
          url
        }
      }
    `,
    "GET",
    undefined,
    {
      queryKey: ["mapData"],
      enabled: !staticDataUrl,
      initialData: { points: [], categories: [], groups: [], medias: [] },
      gcTime,
    },
    { skipAuth: true },
  )

  const activeQuery = staticDataUrl ? staticQuery : q

  // [Backend searching]
  // const searchQuery = useGraphqlMutation<{
  //   pointSearch: number[]
  // }>(
  //   "pointSearch",
  //   gql`
  //     query pointSearch($search: String!) {
  //       pointSearch(search: $search)
  //     }
  //   `,
  //   undefined,
  //   {
  //     recaptcha: true,
  //   },
  // )

  const mapData = useMemo(() => {
    const categoryPointIdsMap = new Map<number, number[]>()
    activeQuery.data?.points.forEach((p) => {
      if (!categoryPointIdsMap.has(p.categoryId)) {
        categoryPointIdsMap.set(p.categoryId, [])
      }
      categoryPointIdsMap.get(p.categoryId)?.push(p.id)
    })

    const categoryMap: Record<number, ApiCategory> =
      activeQuery.data?.categories.reduce((acc: Record<number, ApiCategory>, category) => {
        acc[category.id] = {
          ...category,
          pointIds: categoryPointIdsMap.get(category.id),
          pointCount: categoryPointIdsMap.get(category.id)?.length,
        }
        return acc
      }, {}) || {}

    const groups: NormalisedGroup[] = (activeQuery.data?.groups || []).map((g) => ({
      ...g,
      categories: Object.entries(categoryMap)
        .filter(([cid, category]) => category.groupId === g.id)
        .map(([cid, category]) => category),
    }))

    const groupMap = groups.reduce((acc: Record<number, NormalisedGroup>, group) => {
      acc[group.id] = group
      return acc
    }, {})

    const mediaMap: Record<number, any> =
      activeQuery.data?.medias.reduce((acc: Record<number, ApiMedia>, media) => {
        acc[media.id] = media
        return acc
      }, {}) || {}

    return {
      ...activeQuery,
      isPending: activeQuery.isPending,
      points: activeQuery.data?.points || [],
      categories: Object.values(categoryMap),
      categoryMap,
      mediaMap,
      groups,
      groupMap,
    }
  }, [activeQuery.isPending, activeQuery.data])

  const fuse = useMemo(() => {
    const options = {
      includeScore: true,
      shouldSort: true,
      threshold: 0.25,
      keys: ["name", "description"],
    }
    return new Fuse<ApiPoint>(activeQuery.data?.points || [], options)
  }, [activeQuery.isPending, activeQuery.data])

  const search = (query: string) => {
    const results = fuse.search(query)
    setSearchResults(results)
  }

  useEffect(() => {
    if (sidebarTab !== "search") setSearchResults([])
  }, [sidebarTab])

  return {
    ...mapData,
    // searchQuery,
    search,
    searchResults,
  }
}

export default useMapData
